/* eslint-disable */
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import useAdminTheme from "../admindashboard/useTheme.js";
import { ThemeProvider, useTheme } from "./theme";
import { RouterProvider } from "./router";
import { TopNavBar, MobileNav } from "./sidebar";
import { PayoraRoutes } from "./pages";
import { cn } from "./utils";
import "./payora.css";

function PayoraIntroCurtain({ show, isLight }) {
  const accent = isLight ? "#f5b700" : "#ffc927";
  const accentSoft = isLight ? "rgba(245,183,0,0.82)" : "rgba(255,201,39,0.86)";
  const primary = isLight ? "#1f63e8" : "#3f7dff";
  const pageBg = isLight ? "#ffffff" : "#081229";
  const pageBgGradient = isLight
    ? "radial-gradient(1200px 800px at 12% -10%, rgba(199,219,255,0.95) 0%, rgba(255,255,255,0) 55%), radial-gradient(1100px 700px at 100% 120%, rgba(255,235,159,0.92) 0%, rgba(255,255,255,0) 55%), linear-gradient(135deg,#e8efff 0%, #ffffff 45%, #fff8db 100%)"
    : "radial-gradient(1200px 800px at 12% -10%, rgba(63,125,255,0.35) 0%, rgba(8,18,41,0) 55%), radial-gradient(1100px 700px at 100% 120%, rgba(255,201,39,0.28) 0%, rgba(8,18,41,0) 55%), radial-gradient(900px 600px at 50% 120%, rgba(63,125,255,0.22) 0%, rgba(8,18,41,0) 60%)";
  const titleClass = isLight
    ? "mt-6 font-display text-3xl font-semibold tracking-tight text-slate-900"
    : "mt-6 font-display text-3xl font-semibold tracking-tight text-white";
  const captionClass = isLight
    ? "mt-3 font-display text-xs text-slate-500 italic"
    : "mt-3 font-display text-xs italic";
  const dividerBg = isLight ? "rgba(15,23,42,0.12)" : "rgba(255,255,255,0.08)";
  const tileCls = isLight
    ? "relative grid size-20 place-items-center rounded-[28px] p-2.5 border bg-white"
    : "relative grid size-20 place-items-center rounded-[28px] bg-white p-2.5 border border-white/10";
  const tileStyle = isLight
    ? {
        borderColor: "rgba(15,23,42,0.08)",
        boxShadow: "0 2px 4px rgba(15,23,42,0.08), 0 24px 48px -20px rgba(31,99,232,0.28)",
      }
    : {
        boxShadow:
          "0 24px 60px -18px rgba(250,204,21,0.35), 0 0 0 1px rgba(250,204,21,0.15) inset",
      };
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="payora-intro-curtain"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06, filter: "blur(10px)" }}
          transition={{ duration: 0.75, ease: [0.65, 0, 0.35, 1] }}
          className="absolute inset-0 z-[140] grid place-items-center"
          style={{
            background: pageBgGradient || pageBg,
            backgroundColor: pageBg,
          }}
        >
          <div className="flex flex-col items-center">
            <motion.span
              initial={{ opacity: 0, scale: 0.6, rotate: -22 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 150, damping: 13, delay: 0.05 }}
              className={tileCls}
              style={tileStyle}
            >
              <motion.span
                className="absolute inset-0 rounded-[28px] border-2"
                style={{ borderColor: accent }}
                initial={{ opacity: 0.9, scale: 1 }}
                animate={{ opacity: 0, scale: 1.55 }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeOut" }}
              />
              <img src="/iSource LOGO1.png" alt="iSource" className="w-full h-full object-contain" />
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className={titleClass}
              style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}
            >
              iSource Payora
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.42, ease: [0.22, 1, 0.36, 1] }}
              className="mt-2 text-[10px] font-bold tracking-[0.34em] uppercase"
              style={{ color: accentSoft }}
            >
              Payroll Processing Hub
            </motion.p>

            <div
              className="relative mt-8 h-px w-56 overflow-hidden"
              style={{ background: dividerBg }}
            >
              <motion.span
                className="absolute inset-y-0 left-0"
                style={{ background: accent }}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 1.2, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              />
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={captionClass}
              style={{ color: isLight ? "rgba(15,23,42,0.55)" : accentSoft }}
            >
              Loading payroll management tools…
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// Embeds the Payora payroll suite inside the New Admin Dashboard.
function PayoraShell({ showIntro }) {
  const { theme } = useTheme();
  const { isLight } = useAdminTheme();
  return (
    <div
      className={cn(
        "payora-app relative overflow-hidden",
        theme === "light" && "theme-light"
      )}
      data-testid="payora-app"
      data-theme={theme}
      style={{ minHeight: "calc(100vh - 180px)", height: "calc(100vh - 180px)", display: "flex", flexDirection: "column" }}
    >
      <div className="app-backdrop" aria-hidden />
      <PayoraIntroCurtain show={showIntro} isLight={isLight} />
      <div className="relative z-10 flex h-full min-h-0 flex-col">
        <TopNavBar />
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <main className="flex-1 overflow-y-auto px-4 pt-5 pb-8 sm:px-6 lg:px-10 lg:pt-7">
            <div className="mx-auto w-full max-w-[1400px]">
              <PayoraRoutes />
            </div>
          </main>
          <MobileNav />
        </div>
      </div>
    </div>
  );
}

export default function PayoraApp() {
  const { isLight } = useAdminTheme();
  const forcedTheme = isLight ? "light" : "dark";
  const [minElapsed, setMinElapsed] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMinElapsed(true), 1400);
    return () => clearTimeout(t);
  }, []);

  return (
    <ThemeProvider defaultTheme={forcedTheme}>
      <RouterProvider>
        <PayoraShell showIntro={!minElapsed} />
      </RouterProvider>
    </ThemeProvider>
  );
}
